import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '../../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import WorkflowDialog from './WorkflowDialog';
import * as manufacturingApi from '../../../lib/api/manufacturing';

const WorkflowList = () => {
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ===========================================
  // 데이터 로드
  // ===========================================
  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manufacturingApi.getWorkflows({ page: 1, size: 100 });
      console.log('워크플로우 목록 API 응답:', response.data);
      
      if (response.data.success) {
        const list = response.data.data?.content || response.data.data || [];
        // 프론트엔드 형식으로 변환
        const formattedList = list.map(item => ({
          id: item.workflowUuid,
          workflowUuid: item.workflowUuid,
          workflowCode: item.workflowCd,
          workflowName: item.workflowNm,
          description: item.workflowDesc || '-',
          stepCount: item.stepCount || 0,
          usageCount: item.usageCount || 0,
          isDefault: item.isDefault === 'Y',
          useYn: item.useYn,
          createdBy: item.crtUsrNm || '-',
          createdDate: item.crtDt ? item.crtDt.substring(0, 10) : '-',
          details: item.details || [],
        }));

        // 기본 워크플로우를 맨 위로, 그다음은 최신순.
        //
        // 서버도 같은 순서로 보내주지만(IS_DEFAULT DESC, CRT_DT DESC) 여기서 한 번 더 맞춘다.
        // 목록을 부르는 곳이 여럿이라 어느 한 곳이 정렬 없는 응답을 주면 순서가 조용히 흐트러진다.
        // 사용자 눈에는 "기준 없이 뒤죽박죽" 으로만 보여서 원인을 찾기 어렵다.
        formattedList.sort((a, b) => {
          if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
          return String(b.createdDate).localeCompare(String(a.createdDate));
        });

        setWorkflows(formattedList);
      } else {
        showSnackbar(response.data.message || '워크플로우 목록 조회 실패', 'error');
      }
    } catch (error) {
      console.error('워크플로우 목록 조회 에러:', error);
      showSnackbar('워크플로우 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // ===========================================
  // 스낵바
  // ===========================================
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ===========================================
  // 이벤트 핸들러
  // ===========================================
  const handleAddNew = () => {
    setSelectedWorkflow(null);
    setOpenDialog(true);
  };

  const handleEdit = async (workflow) => {
    try {
      // 상세 정보 조회 (공정 순서 포함)
      const response = await manufacturingApi.getWorkflow(workflow.workflowUuid);
      if (response.data.success) {
        const data = response.data.data;
        setSelectedWorkflow({
          ...workflow,
          details: data.details || [],
        });
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('워크플로우 상세 조회 에러:', error);
      showSnackbar('워크플로우 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  const handleCopy = async (workflow) => {
    try {
      // 상세 정보 조회 (공정 순서 포함)
      const response = await manufacturingApi.getWorkflow(workflow.workflowUuid);
      if (response.data.success) {
        const data = response.data.data;
        const copiedWorkflow = {
          ...workflow,
          id: null,
          workflowUuid: null,
          workflowCode: null,
          workflowName: `${workflow.workflowName} (복사본)`,
          usageCount: 0,
          isDefault: false,
          details: data.details || [],
        };
        setSelectedWorkflow(copiedWorkflow);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('워크플로우 복사 에러:', error);
      showSnackbar('워크플로우 복사에 실패했습니다.', 'error');
    }
  };

  /**
   * 기본 워크플로우 지정.
   *
   * 발주 단계에서 생산계획을 자동으로 만들 때 이걸 쓴다. 사람이 고를 수 없으니
   * 기준이 하나 있어야 한다.
   *
   * 한 건만 기본이 되도록 내리는 일은 서버가 같이 처리한다.
   * 화면에서 하나씩 바꾸면 중간에 둘 다 기본인 순간이 생긴다.
   */
  const handleSetDefault = async (workflow) => {
    if (workflow.isDefault) return;

    try {
      const response = await manufacturingApi.setDefaultWorkflow(workflow.workflowUuid);
      if (response.data.success) {
        showSnackbar(`'${workflow.workflowNm || workflow.workflowName}' 을(를) 기본으로 지정했습니다.`);
        fetchWorkflows();
      } else {
        showSnackbar(response.data.message || '기본 워크플로우 지정에 실패했습니다.', 'error');
      }
    } catch (error) {
      const message = error?.response?.data?.message || '기본 워크플로우 지정에 실패했습니다.';
      showSnackbar(message, 'error');
    }
  };

  const handleDelete = async (workflow) => {
    if (workflow.isDefault) {
      showSnackbar('기본 워크플로우는 삭제할 수 없습니다.', 'warning');
      return;
    }
    
    if (workflow.usageCount > 0) {
      showSnackbar('사용 중인 워크플로우는 삭제할 수 없습니다.', 'warning');
      return;
    }

    if (!window.confirm(`"${workflow.workflowName}" 워크플로우를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await manufacturingApi.deleteWorkflow(workflow.workflowUuid);
      if (response.data.success) {
        showSnackbar('워크플로우가 삭제되었습니다.', 'success');
        fetchWorkflows();
      } else {
        showSnackbar(response.data.message || '삭제 실패', 'error');
      }
    } catch (error) {
      console.error('워크플로우 삭제 에러:', error);
      showSnackbar('워크플로우 삭제에 실패했습니다.', 'error');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWorkflow(null);
  };

  const handleSave = async (workflowData) => {
    try {
      // API 요청 데이터 형식으로 변환
      const requestData = {
        workflowNm: workflowData.workflowName,
        workflowDesc: workflowData.description,
        workflowTyp: 'STANDARD',
        useYn: 'Y',
        isDefault: workflowData.isDefault ? 'Y' : 'N',
        details: workflowData.details || [],
      };

      let response;
      if (selectedWorkflow?.workflowUuid) {
        // 수정
        response = await manufacturingApi.updateWorkflow(selectedWorkflow.workflowUuid, requestData);
      } else {
        // 신규 생성
        response = await manufacturingApi.createWorkflow(requestData);
      }

      if (response.data.success) {
        const savedMessage = selectedWorkflow?.workflowUuid ? '워크플로우가 수정되었습니다.' : '워크플로우가 생성되었습니다.';

        // 저장 자체는 성공했으므로 severity를 error로 쓰면 안 된다. 경고가 있으면
        // "저장됨 + 경고" 를 한 스낵바에 같이 담는다. Snackbar는 한 번에 하나만 보이므로
        // 따로 두 번 띄우면 뒤엣것이 앞엣것을 덮어써 성공 메시지가 안 보인다.
        // (예: 제조 공정은 있는데 검사 공정이 없는 워크플로우를 저장한 경우)
        const warnings = response.data.warnings;
        if (Array.isArray(warnings) && warnings.length > 0) {
          showSnackbar(`${savedMessage} ${warnings.join(' ')}`, 'warning');
        } else {
          showSnackbar(savedMessage, 'success');
        }
        handleCloseDialog();
        fetchWorkflows();
      } else {
        showSnackbar(response.data.message || '저장 실패', 'error');
      }
    } catch (error) {
      console.error('워크플로우 저장 에러:', error);
      showSnackbar('워크플로우 저장에 실패했습니다.', 'error');
    }
  };

  // ===========================================
  // 컬럼 정의
  // ===========================================
  const columns = useMemo(
    () => [
      {
        field: 'workflowCode',
        headerName: '워크플로우 코드',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              color: '#1976D2',
            }}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'workflowName',
        headerName: '워크플로우명',
        width: 200,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {params.row.isDefault && (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
            <Typography variant="body2" fontWeight={params.row.isDefault ? 600 : 400}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'description',
        headerName: '공정 설명',
        minWidth: 300,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'stepCount',
        headerName: '공정 단계',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={`${params.value}단계`}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
      {
        field: 'useYn',
        headerName: '사용',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value === 'Y' ? '사용' : '미사용'}
            size="small"
            color={params.value === 'Y' ? 'success' : 'default'}
            variant={params.value === 'Y' ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'createdBy',
        headerName: '작성자',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'createdDate',
        headerName: '작성일',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'actions',
        headerName: '작업',
        width: 190,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              color={params.row.isDefault ? 'warning' : 'default'}
              onClick={() => handleSetDefault(params.row)}
              disabled={params.row.isDefault || params.row.useYn !== 'Y'}
              title={
                params.row.isDefault
                  ? '기본 워크플로우입니다'
                  : params.row.useYn !== 'Y'
                  ? '사용 중인 워크플로우만 기본으로 지정할 수 있습니다'
                  : '기본으로 지정'
              }
            >
              {params.row.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row)}
              title="수정"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleCopy(params.row)}
              title="복사"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row)}
              disabled={params.row.isDefault || params.row.usageCount > 0}
              title={
                params.row.isDefault
                  ? '기본 워크플로우는 삭제할 수 없습니다'
                  : params.row.usageCount > 0
                  ? '사용 중인 워크플로우는 삭제할 수 없습니다'
                  : '삭제'
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  // ===========================================
  // 렌더링
  // ===========================================
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">생산 공정 워크플로우 관리</Typography>
          <Typography variant="body2" color="text.secondary">
            생산 계획에서 사용할 공정 워크플로우를 관리합니다
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchWorkflows}
            disabled={loading}
          >
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            워크플로우 추가
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <CheckCircleIcon
            fontSize="small"
            sx={{ verticalAlign: 'middle', mr: 0.5 }}
          />
          표시는 기본 워크플로우입니다. 사용 중인 워크플로우는 삭제할 수 없습니다.
        </Typography>
      </Alert>

      <Paper elevation={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={workflows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
                // 정렬을 정해두지 않는다. 서버가 보내준 순서(기본이 맨 위, 그다음 최신순)를
                // 그대로 쓰기 위해서다.
                //
                // 예전에는 여기서 작성일 내림차순을 걸어놨다. 그런데 그러면 서버가 맨 위로
                // 올려보낸 기본 워크플로우가 작성일에 밀려 중간에 박힌다. 실제로 기본을
                // 지정했는데 두 번째 줄에 있는 것처럼 보였고, 무슨 기준인지 알 수 없었다.
                //
                // 사용자가 머리글을 눌러 직접 정렬하면 그때는 그 기준을 따른다. 그건 의도한 동작이다.
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              localeText={{
                noRowsLabel: '등록된 워크플로우가 없습니다.',
              }}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          )}
        </Box>
      </Paper>

      <WorkflowDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSave}
        selectedWorkflow={selectedWorkflow}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowList;
